import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemText,
  Divider,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
  useTheme,
  Chip,
  Avatar,
  ListItemAvatar
} from '@mui/material';
import {
  WhatsApp as WhatsAppIcon,
  Message as MessageIcon,
  People as PeopleIcon,
  Campaign as CampaignIcon,
  MoreVert as MoreVertIcon,
  SmartToy as AssistantIcon,
  TrendingUp as TrendingUpIcon,
  Info as InfoIcon,
  AccountBalance as AccountBalanceIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Pause as PauseIcon
} from '@mui/icons-material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area
} from 'recharts';
import DashboardLayout from '../../components/DashboardLayout';
import { useAuth } from '../../hooks/useAuth';
import dashboardService from '../../services/dashboardService';
import { format, parseISO } from 'date-fns';

const Dashboard = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);

  // Colors for charts
  const COLORS = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.error.main,
    theme.palette.info.main,
    '#8884d8',
    '#82ca9d',
    '#ffc658',
    '#ff7300'
  ];

  // Fetch dashboard data from new API
  const fetchDashboardData = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const data = await dashboardService.getDashboardData();
      setDashboardData(data);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data. Please try again later.');
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Handle refresh
  const handleRefresh = useCallback(() => {
    fetchDashboardData(true);
  }, [fetchDashboardData]);

  // Memoized stats cards data
  const statsCards = useMemo(() => {
    if (!dashboardData?.summary) return [];

    const { summary } = dashboardData;
    return [
      {
        title: 'WhatsApp Accounts',
        primaryValue: summary.instances?.connected || 0,
        secondaryValue: summary.instances?.total || 0,
        primaryLabel: 'Connected',
        secondaryLabel: 'Total',
        icon: <WhatsAppIcon color="primary" fontSize="large" />,
        color: COLORS[0]
      },
      {
        title: 'Assistants',
        primaryValue: summary.assistants?.active || 0,
        secondaryValue: summary.assistants?.total || 0,
        primaryLabel: 'Active',
        secondaryLabel: 'Total',
        icon: <AssistantIcon color="primary" fontSize="large" />,
        color: COLORS[1]
      },
      {
        title: 'Contacts',
        primaryValue: summary.contacts?.total || 0,
        secondaryValue: null,
        primaryLabel: 'Total',
        secondaryLabel: null,
        icon: <PeopleIcon color="primary" fontSize="large" />,
        color: COLORS[2]
      },
      {
        title: 'Messages',
        primaryValue: summary.messages?.total || 0,
        secondaryValue: summary.messages?.byAssistant || 0,
        primaryLabel: 'Total',
        secondaryLabel: 'By Assistant',
        icon: <MessageIcon color="primary" fontSize="large" />,
        color: COLORS[3]
      },
      {
        title: 'Campaigns',
        primaryValue: summary.campaigns?.active || 0,
        secondaryValue: summary.campaigns?.total || 0,
        primaryLabel: 'Active',
        secondaryLabel: 'Total',
        icon: <CampaignIcon color="primary" fontSize="large" />,
        color: COLORS[4]
      },
      {
        title: 'Balance',
        primaryValue: summary.finance?.currentBalance || 0,
        secondaryValue: null,
        primaryLabel: summary.finance?.currentBalanceCurrency || 'USD',
        secondaryLabel: null,
        icon: <AccountBalanceIcon color="primary" fontSize="large" />,
        color: COLORS[5]
      }
    ];
  }, [dashboardData, COLORS]);

  // Memoized chart data
  const chartData = useMemo(() => {
    if (!dashboardData) return {};

    const { messagesDynamics } = dashboardData;

    return {
      messagesDynamics: messagesDynamics?.map(item => ({
        ...item,
        date: item.date || 'N/A'
      })) || [],

      // Message distribution pie chart - using real data
      messageDistribution: [
        {
          name: 'Total Messages',
          value: messagesDynamics?.reduce((sum, item) => sum + (item.count || 0), 0) || 0,
          fill: COLORS[1]
        }
      ]
    };
  }, [dashboardData, COLORS]);

  // Status icon helper
  const getStatusIcon = useCallback((status) => {
    if (!status) return <InfoIcon color="info" fontSize="small" />;

    switch (status.toLowerCase()) {
      case 'active':
      case 'running':
      case 'connected':
        return <CheckCircleIcon color="success" fontSize="small" />;
      case 'paused':
      case 'inactive':
        return <PauseIcon color="warning" fontSize="small" />;
      case 'stopped':
      case 'disconnected':
        return <CancelIcon color="error" fontSize="small" />;
      default:
        return <InfoIcon color="info" fontSize="small" />;
    }
  }, []);

  // Status color helper
  const getStatusColor = useCallback((status) => {
    if (!status) return 'default';

    switch (status.toLowerCase()) {
      case 'active':
      case 'running':
      case 'connected':
        return 'success';
      case 'paused':
      case 'inactive':
        return 'warning';
      case 'stopped':
      case 'disconnected':
        return 'error';
      default:
        return 'default';
    }
  }, []);

  // Safe format helper for dates
  const safeFormatDate = useCallback((dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(parseISO(dateString), 'MMM dd');
    } catch (error) {
      console.warn('Error formatting date:', dateString, error);
      return 'Invalid Date';
    }
  }, []);

  if (loading) {
    return (
        <DashboardLayout>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
            <CircularProgress size={60} />
          </Box>
        </DashboardLayout>
    );
  }

  if (error) {
    return (
        <DashboardLayout>
          <Alert
              severity="error"
              sx={{ mb: 4 }}
              action={
                <Button color="inherit" size="small" onClick={handleRefresh}>
                  Try Again
                </Button>
              }
          >
            {error}
          </Alert>
        </DashboardLayout>
    );
  }

  return (
      <DashboardLayout>
        {/* Header */}
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" gutterBottom>
              Welcome back, {user?.name || 'User'}!
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Here's what's happening with your WhatsApp campaigns and assistants today.
            </Typography>
          </Box>
          <Button
              variant="outlined"
              startIcon={refreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
              onClick={handleRefresh}
              disabled={refreshing}
          >
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {statsCards.map((stat, index) => (
              <Grid item xs={12} sm={12} md={6} lg={4} key={index}>
                <Paper
                    elevation={2}
                    sx={{
                      p: 2,
                      display: 'flex',
                      alignItems: 'center',
                      height: '100%',
                      background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`,
                      borderLeft: `4px solid ${stat.color}`,
                      transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: theme.shadows[8]
                      }
                    }}
                >
                  <Box sx={{ mr: 2, color: stat.color }}>
                    {stat.icon}
                  </Box>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {stat.title}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
                          {typeof stat.primaryValue === 'number'
                              ? stat.primaryValue.toLocaleString()
                              : (stat.primaryValue || 0)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {stat.primaryLabel}
                        </Typography>
                      </Box>
                      {stat.secondaryValue !== null && (
                          <Box sx={{ textAlign: 'right', ml: 2 }}>
                            <Typography variant="h5" component="div">
                              {(stat.secondaryValue || 0).toLocaleString()}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {stat.secondaryLabel}
                            </Typography>
                          </Box>
                      )}
                    </Box>
                  </Box>
                </Paper>
              </Grid>
          ))}
        </Grid>

        {/* Charts */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Messages Dynamics */}
          <Grid item xs={12} lg={8}>
            <Card sx={{ height: '100%' }}>
              <CardHeader
                  title={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <TrendingUpIcon sx={{ mr: 1 }} />
                      <Typography variant="h6">Messages Sent (30 days)</Typography>
                    </Box>
                  }
                  action={
                    <Tooltip title="Shows the number of messages sent over the last 30 days">
                      <IconButton>
                        <InfoIcon />
                      </IconButton>
                    </Tooltip>
                  }
              />
              <Divider />
              <CardContent>
                <ResponsiveContainer width="100%" height={350}>
                  <AreaChart data={chartData.messagesDynamics}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <RechartsTooltip formatter={(value) => (value || 0).toLocaleString()} />
                    <Legend />
                    <Area
                        type="monotone"
                        dataKey="count"
                        stackId="1"
                        name="Messages Sent"
                        stroke={COLORS[1]}
                        fill={COLORS[1]}
                        fillOpacity={0.6}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Message Distribution */}
          <Grid item xs={12} lg={4}>
            <Card sx={{ height: '100%' }}>
              <CardHeader
                  title={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <MessageIcon sx={{ mr: 1 }} />
                      <Typography variant="h6">Message Distribution</Typography>
                    </Box>
                  }
                  action={
                    <Tooltip title="Shows the distribution of message types">
                      <IconButton>
                        <InfoIcon />
                      </IconButton>
                    </Tooltip>
                  }
              />
              <Divider />
              <CardContent>
                <ResponsiveContainer width="100%" height={350}>
                  <PieChart>
                    <Pie
                        data={chartData.messageDistribution}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={120}
                        fill="#8884d8"
                        dataKey="value"
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    >
                      {chartData.messageDistribution.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <RechartsTooltip formatter={(value) => (value || 0).toLocaleString()} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Assistant Performance */}
          <Grid item xs={12}>
            <Card>
              <CardHeader
                  title={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <AssistantIcon sx={{ mr: 1 }} />
                      <Typography variant="h6">Assistant Performance</Typography>
                    </Box>
                  }
                  action={
                    <Tooltip title="Shows the performance metrics for each assistant">
                      <IconButton>
                        <InfoIcon />
                      </IconButton>
                    </Tooltip>
                  }
              />
              <Divider />
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={dashboardData?.assistantsStats || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <RechartsTooltip formatter={(value) => (value || 0).toLocaleString()} />
                    <Legend />
                    <Bar dataKey="sentMessagesCount" name="Messages Sent" fill={COLORS[0]} />
                    <Bar dataKey="chatsCount" name="Chats Handled" fill={COLORS[1]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Lists */}
        <Grid container spacing={3}>
          {/* Assistants List */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader
                  title={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <AssistantIcon sx={{ mr: 1 }} />
                      <Typography variant="h6" component="div">
                        Assistants Overview
                      </Typography>
                    </Box>
                  }
                  action={
                    <IconButton aria-label="settings">
                      <MoreVertIcon />
                    </IconButton>
                  }
              />
              <Divider />
              <CardContent sx={{ p: 0 }}>
                <List>
                  {dashboardData?.assistantsStats?.slice(0, 5).map((assistant, index) => (
                      <React.Fragment key={assistant.id || index}>
                        <ListItem>
                          <ListItemAvatar>
                            <Avatar sx={{ bgcolor: COLORS[index % COLORS.length] }}>
                              <AssistantIcon />
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                              primaryTypographyProps={{ component: 'div' }}
                              secondaryTypographyProps={{ component: 'div' }}
                              primary={
                                <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="subtitle1" component="span">
                                    {assistant.name || 'Unnamed Assistant'}
                                  </Typography>
                                  <Chip
                                      icon={getStatusIcon(assistant.status)}
                                      label={assistant.status || 'Unknown'}
                                      size="small"
                                      color={getStatusColor(assistant.status)}
                                      variant="outlined"
                                  />
                                </Box>
                              }
                              secondary={
                                <Box component="span" sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                                  <Typography variant="body2" component="span" color="text.secondary">
                                    {assistant.chatsCount || 0} chats
                                  </Typography>
                                  <Typography variant="body2" component="span" color="text.secondary">
                                    {assistant.sentMessagesCount || 0} messages sent
                                  </Typography>
                                </Box>
                              }
                          />
                        </ListItem>
                        {index < Math.min((dashboardData?.assistantsStats?.length || 0), 5) - 1 && <Divider />}
                      </React.Fragment>
                  )) || []}
                  {(!dashboardData?.assistantsStats || dashboardData.assistantsStats.length === 0) && (
                      <ListItem>
                        <ListItemText
                            primaryTypographyProps={{ component: 'div' }}
                            secondaryTypographyProps={{ component: 'div' }}
                            primary="No assistants found"
                            secondary="Create your first assistant to get started"
                        />
                      </ListItem>
                  )}
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Campaigns List */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader
                  title={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CampaignIcon sx={{ mr: 1 }} />
                      <Typography variant="h6">Campaigns Overview</Typography>
                    </Box>
                  }
                  action={
                    <IconButton aria-label="settings">
                      <MoreVertIcon />
                    </IconButton>
                  }
              />
              <Divider />
              <CardContent sx={{ p: 0 }}>
                <List>
                  {dashboardData?.campaignsStats?.slice(0, 5).map((campaign, index) => (
                      <React.Fragment key={campaign.id || index}>
                        <ListItem>
                          <ListItemAvatar>
                            <Avatar sx={{ bgcolor: COLORS[index % COLORS.length] }}>
                              <CampaignIcon />
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                              primaryTypographyProps={{ component: 'div' }}
                              secondaryTypographyProps={{ component: 'div' }}
                              primary={
                                <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="subtitle1" component="span">
                                    {campaign.name || 'Unnamed Campaign'}
                                  </Typography>
                                  <Chip
                                      icon={getStatusIcon(campaign.status)}
                                      label={campaign.status || 'Unknown'}
                                      size="small"
                                      color={getStatusColor(campaign.status)}
                                      variant="outlined"
                                  />
                                </Box>
                              }
                              secondary={
                                <Box component="span" sx={{ display: 'flex', gap: 2, mt: 0.5, flexWrap: 'wrap' }}>
                                  <Typography variant="body2" component="span" color="text.secondary">
                                    {campaign.contactsCount || 0} contacts
                                  </Typography>
                                  <Typography variant="body2" component="span" color="text.secondary">
                                    {campaign.instancesCount || 0} instances
                                  </Typography>
                                  {campaign.period && campaign.period.start && campaign.period.end && (
                                      <Typography variant="body2" component="span" color="text.secondary">
                                        {safeFormatDate(campaign.period.start)} - {safeFormatDate(campaign.period.end)}
                                      </Typography>
                                  )}
                                </Box>
                              }
                          />
                        </ListItem>
                        {index < Math.min((dashboardData?.campaignsStats?.length || 0), 5) - 1 && <Divider />}
                      </React.Fragment>
                  )) || []}
                  {(!dashboardData?.campaignsStats || dashboardData.campaignsStats.length === 0) && (
                      <ListItem>
                        <ListItemText
                            primaryTypographyProps={{ component: 'div' }}
                            secondaryTypographyProps={{ component: 'div' }}
                            primary="No campaigns found"
                            secondary="Create your first campaign to get started"
                        />
                      </ListItem>
                  )}
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </DashboardLayout>
  );
};

export default Dashboard;